import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Calendar, Users, ShoppingBag, Mail, Instagram, Phone, MapPin, Menu, X, Recycle, Briefcase, LogOut, Building2, User as UserIcon, Map } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChangemakerAuth } from '../context/ChangemakerAuthContext';
import { track } from '../utils/analytics';

const Avatar = ({ user, size = 24 }) => {
  const pad = Math.round(size * 0.18); // 18% inset keeps icon fully inside the circle
  const iconSize = size - pad * 2;

  if (user?.profile_pic) {
    return (
      <img src={user.profile_pic} alt={user.name} className="rounded-full object-cover" style={{ width: size, height: size }} />
    );
  }

  if (user) {
    /* Logged-in, no photo — dark slate circle with white person icon (reference image inverted) */
    return (
      <div style={{ width: size, height: size, background: '#374151', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <img
          src="/images/avatar-loggedin.png"
          alt=""
          style={{ width: iconSize, height: iconSize, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
        />
      </div>
    );
  }

  /* Not logged in — light grey circle with dark outline person icon */
  return (
    <div style={{ width: size, height: size, background: '#e5e7eb', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      <img
        src="/images/avatar-loggedout.png"
        alt=""
        style={{ width: iconSize, height: iconSize, objectFit: 'contain' }}
      />
    </div>
  );
};

const NAV_LINKS = [
  { label: 'Civic Activities',    href: '/activities',       Icon: Calendar },
  { label: 'Donate Used Items',   href: '/services/donate-used-items', Icon: Recycle  },
  { label: 'Jobs & Internships',  href: null,                 Icon: Briefcase, soon: true },
  { label: 'Changemakers',        href: '/organizations',     Icon: Users    },
];

function HamburgerDrawer({ open, onClose }) {
  const location = useLocation();
  const drawerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (drawerRef.current && !drawerRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black/30 z-[60] transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />

      {/* Drawer */}
      <div
        ref={drawerRef}
        data-testid="ham-drawer"
        className={`fixed top-0 left-0 h-full w-72 bg-white z-[70] shadow-2xl flex flex-col transition-transform duration-250 ease-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <Link to="/" onClick={() => { track('nav_hamburger_link', { link: '/', label: 'Home' }); onClose(); }} className="flex items-center gap-2.5">
            <img src="/images/logo.webp" alt="Karmana" className="h-9 w-9 object-contain rounded-full" />
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-gray-900">Karmana</span>
              <span className="text-[10px] text-gray-400">Bengaluru's Civic Action Platform</span>
            </div>
          </Link>
          <button onClick={onClose} data-testid="ham-close-btn" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV_LINKS.map(({ label, href, Icon, soon }) => {
            const active = href && location.pathname.startsWith(href);
            if (soon) {
              return (
                <div key={label}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 cursor-not-allowed">
                  <Icon size={19} />
                  <span className="text-sm font-medium">{label}</span>
                  <span className="ml-auto text-[10px] bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-medium">Soon</span>
                </div>
              );
            }
            return (
              <Link key={label} to={href} onClick={() => { track('nav_hamburger_link', { link: href, label }); onClose(); }} data-testid={`ham-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium ${
                  active ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}>
                <Icon size={19} className={active ? 'text-green-600' : 'text-gray-400'} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* My Journeys link in hamburger */}
        <div className="px-3 pb-2 border-t border-gray-100 pt-3">
          <Link to="/my-journeys" onClick={onClose} data-testid="ham-link-my-journeys"
            className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900">
            <Map size={19} className="text-gray-400" />
            My Journeys
          </Link>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} Karmana</p>
        </div>
      </div>
    </>
  );
}

const Layout = ({ children, noDefaultBg, mobileHeaderLeft, headerCenter }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, requireAuth, logout } = useAuth();
  const { cmUser, cmOrg } = useChangemakerAuth();
  const [scrolled, setScrolled]         = useState(false);
  const [hamOpen, setHamOpen]           = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setHamOpen(false); setDropdownOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close dropdown on outside click (works for both desktop and mobile)
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = () => setDropdownOpen(false);
    const timer = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handler); };
  }, [dropdownOpen]);

  const goMyImpact = () => {
    if (!user) {
      track('engagement_action', { action: 'signup_button_click' });
      track('signup_trigger', { trigger: 'signin_button', page: window.location.pathname.replace('/', '') || 'home' });
    }
    requireAuth(() => navigate('/my-impact'), 'login_button');
  };

  const handleProfileClick = () => {
    track('nav_profile_click', { is_authenticated: !!(user || cmUser) });
    if (!user && !cmUser) { goMyImpact(); return; }
    setDropdownOpen(v => !v);
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate('/');
  };

  const handleToggleToCm = () => {
    if (cmOrg) navigate(`/organizations/${cmOrg.slug || cmOrg.id}`);
  };

  const HamBtn = ({ size = 20 }) => (
    <button onClick={() => { track('nav_hamburger_open', {}); setHamOpen(true); }} data-testid="ham-btn"
      className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0">
      <Menu size={size} />
    </button>
  );

  return (
    <div id="layout-main-wrapper" className={`min-h-screen ${noDefaultBg ? '' : 'bg-gray-50'}`}>
      {/* Hamburger drawer (portal-like, above everything) */}
      <HamburgerDrawer open={hamOpen} onClose={() => setHamOpen(false)} />

      {/* ── Top Navigation ── */}
      <header className={`bg-white border-b border-gray-200 sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-sm' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* ── DESKTOP ── */}
          <div className="hidden md:block">
            {headerCenter ? (
              /* When a search bar is provided, mirror the main content layout so
                 the bar aligns exactly with the activity feed column */
              <div className="flex items-center h-16 gap-10">
                {/* "Sidebar" area — same width as the filter sidebar (w-64) */}
                <div className="w-64 flex-shrink-0 flex items-center gap-2">
                  <HamBtn />
                  <Link to="/" className="flex items-center gap-1.5 ml-1" aria-label="Home">
                    <img src="/images/logo.webp" alt="Karmana" className="h-7 w-7 object-contain rounded-full flex-shrink-0" />
                  </Link>
                </div>
                {/* Content area — flex-1 mirrors the activity feed column */}
                <div className="flex-1 min-w-0">
                  <div className="max-w-[624px] mx-auto">
                    {headerCenter}
                  </div>
                </div>
                {/* Profile */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {user?.is_admin && (
                    <Link to="/admin" className="text-sm font-medium text-gray-600 hover:text-green-600 px-3 py-2 rounded-lg hover:bg-gray-50" data-testid="admin-link">
                      Admin
                    </Link>
                  )}
                  <div className="relative">
                    <button
                      onClick={handleProfileClick}
                      data-testid="desktop-myimpact-btn"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50"
                    >
                      <Avatar user={cmUser || user} size={28} />
                      <div className="flex flex-col items-start leading-tight">
                        <span className="text-sm font-medium text-gray-700">
                          {cmUser ? cmUser.name?.split(' ')[0] : (user ? user.name?.split(' ')[0] : 'Sign In')}
                        </span>
                        {cmUser && cmOrg && (
                          <span className="text-[9px] font-medium text-green-600" data-testid="cm-header-label">{cmOrg.name}</span>
                        )}
                      </div>
                    </button>
                    {dropdownOpen && (user || cmUser) && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-[80]" data-testid="profile-dropdown">
                        {cmUser && cmOrg ? (
                          <>
                            <button onClick={() => { setDropdownOpen(false); navigate(`/organizations/${cmOrg.slug || cmOrg.id}`); }}
                              data-testid="dropdown-org-profile"
                              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                              <Building2 size={15} className="text-gray-400" />
                              Org Profile
                            </button>
                            <button onClick={() => { setDropdownOpen(false); navigate('/my-impact'); }}
                              data-testid="dropdown-user-profile"
                              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                              <UserIcon size={15} className="text-gray-400" />
                              User Profile
                            </button>
                          </>
                        ) : (
                          <button onClick={() => { setDropdownOpen(false); navigate('/my-impact'); }}
                            data-testid="dropdown-view-profile"
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                            <UserIcon size={15} className="text-gray-400" />
                            View Profile
                          </button>
                        )}
                        <div className="my-1 border-t border-gray-100" />
                        <button onClick={() => { setDropdownOpen(false); navigate('/my-journeys'); }}
                          data-testid="dropdown-my-journeys"
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <Map size={15} className="text-gray-400" />
                          My Journeys
                        </button>
                        <div className="my-1 border-t border-gray-100" />
                        <button onClick={handleLogout}
                          data-testid="dropdown-logout"
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                          <LogOut size={15} className="text-red-400" />
                          Log Out
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Default layout: hamburger left, logo centered, profile right */
              <div className="relative flex items-center justify-center h-16">
                <div className="absolute left-0 flex items-center gap-2">
                  <HamBtn />
                </div>

                {/* Center: full Logo */}
                <Link to="/" className="flex items-center gap-2.5">
                  <img src="/images/logo.webp" alt="Karmana" className="h-10 w-10 object-contain rounded-full flex-shrink-0" />
                  <div className="flex flex-col leading-tight">
                    <span className="text-xl font-bold text-gray-900">Karmana</span>
                    <span className="text-xs text-gray-500">Bengaluru's Civic Action Platform</span>
                  </div>
                </Link>

                {/* Profile — always absolute right */}
                <div className="absolute right-0 flex items-center gap-2">
                  {user?.is_admin && (
                    <Link to="/admin" className="text-sm font-medium text-gray-600 hover:text-green-600 px-3 py-2 rounded-lg hover:bg-gray-50" data-testid="admin-link">
                      Admin
                    </Link>
                  )}
                  {/* Profile dropdown */}
                  <div className="relative">
                    <button
                      onClick={handleProfileClick}
                      data-testid="desktop-myimpact-btn"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50"
                    >
                      <Avatar user={cmUser || user} size={28} />
                      <div className="flex flex-col items-start leading-tight">
                        <span className="text-sm font-medium text-gray-700">
                          {cmUser ? cmUser.name?.split(' ')[0] : (user ? user.name?.split(' ')[0] : 'Sign In')}
                        </span>
                        {cmUser && cmOrg && (
                          <span className="text-[9px] font-medium text-green-600" data-testid="cm-header-label">{cmOrg.name}</span>
                        )}
                      </div>
                    </button>

                    {/* Dropdown menu */}
                    {dropdownOpen && (user || cmUser) && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-[80]" data-testid="profile-dropdown">
                        {cmUser && cmOrg ? (
                          <>
                            <button onClick={() => { setDropdownOpen(false); navigate(`/organizations/${cmOrg.slug || cmOrg.id}`); }}
                              data-testid="dropdown-org-profile"
                              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                              <Building2 size={15} className="text-gray-400" />
                              Org Profile
                            </button>
                            <button onClick={() => { setDropdownOpen(false); navigate('/my-impact'); }}
                              data-testid="dropdown-user-profile"
                              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                              <UserIcon size={15} className="text-gray-400" />
                              User Profile
                            </button>
                          </>
                        ) : (
                          <button onClick={() => { setDropdownOpen(false); navigate('/my-impact'); }}
                            data-testid="dropdown-view-profile"
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                            <UserIcon size={15} className="text-gray-400" />
                            View Profile
                          </button>
                        )}
                        <div className="my-1 border-t border-gray-100" />
                        <button onClick={() => { setDropdownOpen(false); navigate('/my-journeys'); }}
                          data-testid="dropdown-my-journeys"
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <Map size={15} className="text-gray-400" />
                          My Journeys
                        </button>
                        <div className="my-1 border-t border-gray-100" />
                        <button onClick={handleLogout}
                          data-testid="dropdown-logout"
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                          <LogOut size={15} className="text-red-400" />
                          Log Out
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── MOBILE ── */}
          <div className="md:hidden relative flex items-center justify-center h-14">
            {/* Hamburger — absolute left, replaces mobileHeaderLeft if present */}
            <div className="absolute left-0 flex items-center gap-1">
              <HamBtn size={20} />
              {mobileHeaderLeft}
            </div>

            {/* Center */}
            {headerCenter ? (
              <div className="w-full pl-[80px] pr-[56px]">
                {headerCenter}
              </div>
            ) : (
              <Link to="/" className="flex items-center gap-2">
                <img src="/images/logo.webp" alt="Karmana" className="h-9 w-9 object-contain rounded-full flex-shrink-0" />
                <div className="flex flex-col leading-tight">
                  <span className="text-lg font-bold text-gray-900">Karmana</span>
                  <span className="text-[10px] text-gray-500">Bengaluru's Civic Action Platform</span>
                </div>
              </Link>
            )}

            {/* Sign In / avatar — absolute right */}
            <div className="absolute right-0 flex items-center gap-1.5">
              <div className="relative">
                <button onClick={handleProfileClick} className="flex flex-col items-center mr-0.5" data-testid="mobile-login-btn">
                  <Avatar user={cmUser || user} size={22} />
                  <span className={`text-[9px] leading-tight mt-0.5 ${!cmUser && !user ? 'text-green-600 font-medium' : 'text-gray-500 font-normal'}`}>
                    {!cmUser && !user ? 'Sign In' : (cmUser ? cmUser.name?.split(' ')[0] : user?.name?.split(' ')[0])}
                  </span>
                </button>

                {/* Mobile dropdown */}
                {dropdownOpen && (user || cmUser) && (
                  <div className="absolute right-0 mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-[80]" data-testid="profile-dropdown-mobile">
                    {cmUser && cmOrg ? (
                      <>
                        <button onClick={() => { setDropdownOpen(false); navigate(`/organizations/${cmOrg.slug || cmOrg.id}`); }}
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                          <Building2 size={14} className="text-gray-400" />
                          Org Profile
                        </button>
                        <button onClick={() => { setDropdownOpen(false); navigate('/my-impact'); }}
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                          <UserIcon size={14} className="text-gray-400" />
                          User Profile
                        </button>
                      </>
                    ) : (
                      <button onClick={() => { setDropdownOpen(false); navigate('/my-impact'); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                        <UserIcon size={14} className="text-gray-400" />
                        View Profile
                      </button>
                    )}
                    <div className="my-1 border-t border-gray-100" />
                    <button onClick={() => { setDropdownOpen(false); navigate('/my-journeys'); }}
                      data-testid="dropdown-my-journeys-mobile"
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                      <Map size={14} className="text-gray-400" />
                      My Journeys
                    </button>
                    <div className="my-1 border-t border-gray-100" />
                    <button onClick={handleLogout}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                      <LogOut size={14} className="text-red-400" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Shared Footer */}
      <footer className="bg-white border-t border-gray-200 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Get in Touch</h2>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-5 sm:gap-8 mb-6">
            <a href="mailto:contact@karmana.in" onClick={() => track('footer_contact_click', { contact_type: 'email' })} className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors">
              <Mail size={18} /><span className="text-sm">contact@karmana.in</span>
            </a>
            <a href="https://www.instagram.com/be_a_karmana/" target="_blank" rel="noopener noreferrer" onClick={() => track('footer_contact_click', { contact_type: 'instagram' })} className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors">
              <Instagram size={18} /><span className="text-sm">@be_a_karmana</span>
            </a>
            <a href="tel:+919793495211" onClick={() => track('footer_contact_click', { contact_type: 'phone' })} className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors">
              <Phone size={18} /><span className="text-sm">+91 97934 95211</span>
            </a>
          </div>
          <div className="border-t border-gray-100 pt-5 flex justify-center gap-6">
            <Link to="/about" onClick={() => track('engagement_action', { action: 'about_us_click' })} className="text-sm text-gray-500 hover:text-green-600 transition-colors">About Us</Link>
          </div>
          <p className="text-xs text-gray-400 mt-4">&copy; {new Date().getFullYear()} Karmana. Bengaluru's Civic Action Platform.</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
